<template>
    <page-layout>
        <g-loader v-if="isLoading" type="fixed" />
        <template v-else>
            <template slot="header">
                <page-title title="Deliver Task" @on-close="onPageClose"/>
            </template>
            <template slot="default">
                <error-message-card v-if="errorMessage" class="margin-bottom-normal" :message="errorMessage" />

                <div v-if="instagramCreatorContactNetwork" class="margin-bottom-large">
                    <contact-network-item
                        class="margin-bottom-small"
                        :contact-network="instagramCreatorContactNetwork"
                        context="campaign"
                    />
                    <g-typography v-if="!isJobDelivered" tag="p">
                        Your Instagram story media and insights should be delivered automatically, but you can still
                        upload your story manually below.
                    </g-typography>
                </div>
                <base-box
                    v-else-if="!showThirdParty"
                    background-color="grey"
                    :border="true"
                    class="margin-bottom-large"
                    data-testid="ig-story-connect-banner"
                >
                    <template slot="content">
                        <div class="margin-bottom-small">
                            <g-typography tag="h3" font-weight="semibold">
                                About delivering Instagram stories
                            </g-typography>
                        </div>
                        <div class="margin-bottom-normal">
                            <g-typography tag="p">
                                If your story has been live for less than 24 hours you can connect your Instagram and
                                skip this upload process
                            </g-typography>
                        </div>
                        <g-button class="connect-button" shape="round" theme="live" @click="connect">
                            {{ connectButtonText }}
                        </g-button>
                    </template>
                </base-box>

                <div v-if="isJobDelivered">
                    <div class="margin-bottom-normal">
                        <div class="margin-bottom-normal">
                            <base-box background-color="green" :border="true" border-color="green">
                                <template slot="content">
                                    <g-typography>
                                        Your media has been delivered. Once your story ends, click "Upload Story
                                        Insights" below
                                    </g-typography>
                                </template>
                            </base-box>
                        </div>

                        <div class="margin-bottom-small">
                            <g-typography tag="h3" font-weight="semibold" value="Upload Your Story Insights" />
                        </div>

                        <div class="margin-bottom-small">
                            <g-typography tag="p">
                                After your Story has ended, report your Story Insights Metrics by providing the
                                screenshot(s) below.
                            </g-typography>
                        </div>
                        <div class="margin-bottom-small">
                            <g-typography tag="p" size="small">
                                Please note that Instagram Story Insights may disappear after 48 hours. For help, visit
                                our <a :href="tutorialUrl" target="_blank">help documentation</a>.
                            </g-typography>
                        </div>
                    </div>

                    <div class="margin-bottom-normal">
                        <g-upload
                            :action="deliverInsightsUrl"
                            :multiple="true"
                            :headers="uploadHeaders"
                            :show-upload-list="{ showRemoveIcon: false }"
                            @change="handleInsightsFileChange"
                        >
                            <g-button shape="round" theme="live" block>
                                <g-icon name="upload" />
                                Upload Story Insights
                            </g-button>
                        </g-upload>
                    </div>

                    <div class="margin-bottom-small">
                        <g-typography tag="p" font-weight="semibold" value="Report Your Story Insights Metrics" />
                    </div>

                    <div class="margin-bottom-normal">
                        <g-typography tag="p" value="Enter your Story Insights metrics." />
                    </div>

                    <g-form class="margin-bottom-normal">
                        <g-form-item label="Impressions" :errors="insights.errors.impressions" required>
                            <g-input v-model="insights.metrics.impressions" size="large" :disabled="insights.sending" />
                        </g-form-item>

                        <g-form-item label="Reach" :errors="insights.errors.reach" required>
                            <g-input v-model="insights.metrics.reach" size="large" :disabled="insights.sending" />
                        </g-form-item>

                        <g-form-item label="Replies" :errors="insights.errors.replies" required>
                            <g-input v-model="insights.metrics.replies" size="large" :disabled="insights.sending" />
                        </g-form-item>

                        <g-form-item label="Taps Forward" :errors="insights.errors.taps_forward" required>
                            <g-input
                                v-model="insights.metrics.taps_forward"
                                size="large"
                                :disabled="insights.sending"
                            />
                        </g-form-item>

                        <g-form-item label="Taps Back" :errors="insights.errors.taps_back" required>
                            <g-input v-model="insights.metrics.taps_back" size="large" :disabled="insights.sending" />
                        </g-form-item>

                        <g-form-item label="Exits" :errors="insights.errors.exits" required>
                            <g-input v-model="insights.metrics.exits" size="large" :disabled="insights.sending" />
                        </g-form-item>
                    </g-form>
                </div>
                <div v-else>
                    <div class="margin-bottom-normal">
                        <div class="margin-bottom-small">
                            <g-typography tag="h3" font-weight="semibold">
                                How to manually deliver your Instagram Story
                            </g-typography>
                        </div>
                        <div class="margin-bottom-small">
                            <g-typography tag="p">
                                There are 2-steps to manually delivering your Instagram Story task after going live on
                                Instagram.
                            </g-typography>
                        </div>
                        <ol class="margin-bottom-normal">
                            <g-typography tag="li">Deliver your Story media file below.</g-typography>
                            <g-typography tag="li">
                                Once your Story has ended, provide your Story Insights screenshot(s) and metrics.
                            </g-typography>
                        </ol>
                        <div class="margin-bottom-small">
                            <g-typography tag="p" size="small">
                                Please note that Instagram Story Insights may disappear after 48 hours. For help, visit
                                our <a :href="tutorialUrl" target="_blank">help documentation</a>.
                            </g-typography>
                        </div>
                    </div>
                    <g-upload
                        :action="deliverStoryUrl"
                        :headers="uploadHeaders"
                        :multiple="false"
                        :show-upload-list="{ showRemoveIcon: false }"
                        @change="handleStoryUploadChange"
                    >
                        <g-button shape="round" theme="live" block>
                            <g-icon name="upload" />
                            Upload Story
                        </g-button>
                    </g-upload>
                </div>
            </template>
            <template slot="footer">
                <g-button
                    shape="round"
                    theme="live"
                    size="large"
                    type="primary"
                    block
                    :disabled="!isJobDelivered || !insightsIsReady || insights.sending"
                    :loading="insights.sending"
                    @click="handleDeliverInsights"
                >
                    Deliver
                </g-button>
            </template>
        </template>
    </page-layout>
</template>

<script>
    import { get } from 'lodash';
    import { message } from '@grininc/grin-ui';
    import PageLayout from '../../layouts/PageLayout.vue';
    import ProposalStepHeader from './partials/ProposalStepHeader.vue';
    import ProposalStepFooter from './partials/ProposalStepFooter.vue';
    import TasksMixin from './mixins/TasksMixin';
    import ErrorMessageCard from './partials/ErrorMessageCard.vue';
    import ProposalStepError from './partials/PoposalStepError.vue';
    import TitleBar from '../../library/TitleBar.vue';
    import ContactNetworkItem from '../networks/partials/ContactNetworkItem.vue';
    import PageTitle from '../../library/PageTitle.vue';
    import NetworkConnectionMixin from '../../../mixins/NetworkConnectionMixin';
    import { SOURCE_STORY_DELIVERY } from '../../../constants/network-connection-sources';
    import { FAQ_DELIVERING_STORY_INSIGHTS_MANUALLY } from '../../../constants/faqs';
    import useStoryInsights from '../../../../src/composables/use-story-insights';
    import StoryInsightsMixin from '../../providers/mixins/StoryInsightsMixin';
    import { useFeatureFlagsStore } from '../../../../stores';

    export default {
        name: 'TaskDeliveryInstagramStoryView',
        components: {
            PageTitle,
            ContactNetworkItem,
            TitleBar,
            ProposalStepError,
            ErrorMessageCard,
            PageLayout,
            ProposalStepHeader,
            ProposalStepFooter,
        },
        mixins: [TasksMixin, NetworkConnectionMixin, StoryInsightsMixin],
        props: {
            campaign: {
                type: Object
            },
            proposal: {
                type: Object
            },
            job: {
                type: Object
            },
            task: {
                type: Object
            },
            contact: {
                type: Object
            },
            isLoading: {
                type: Boolean,
                default: false
            },
            errorMessage: {
                type: String,
                required: false
            },
            deliverStoryUrl: {
                type: String
            },
            deliverInsightsUrl: {
                type: String
            },
            uploadHeaders: {
                type: Object
            },
            isJobDelivered: {
                type: Boolean
            },
            jobHasInsights: {
                type: Boolean
            },
            hasUploadedFiles: {
                type: Boolean,
                default: false
            },
            uploadedFiles: {
                type: Array
            }
        },
        computed: {
            instagramCreatorContactNetwork() {
                return get(this, 'contact.networks', []).find(
                    (contactNetwork) =>
                        contactNetwork.network.name === 'Instagram' &&
                        contactNetwork.network_account_type === 'creator' &&
                        contactNetwork.network_connection_permissions === 'full'
                );
            },
            hasRegularInstagram() {
                return !!get(this, 'proposal.contact.networks', []).find(
                    (contactNetwork) => contactNetwork.network.name === 'Instagram'
                );
            },
            connectButtonText() {
                return this.hasRegularInstagram ? 'Connect Instagram Creator Account' : 'Connect Instagram';
            },
            insightsAlertText() {
                return 'Your media has been delivered. Once your story ends, click "Upload insights"';
            },
            tutorialUrl() {
                return FAQ_DELIVERING_STORY_INSIGHTS_MANUALLY;
            },
            contactId() {
                return get(this, 'contact.id');
            },
            taskId() {
                return get(this, 'task.id');
            },
            network() {
                return get(this, 'task.network');
            },
            showThirdParty() {
                return true;
            }
        },
        methods: {
            connect() {
                this.$emit('start-connection', {
                    name: SOURCE_STORY_DELIVERY,
                    data: { task_id: this.taskId }
                });
            },
            handleStoryUploadChange(info) {
                this.$emit('story-file-change', info);
            },
            handleInsightsFileChange(info) {
                this.$emit('insights-file-change', info);
            },
            async handleDeliverInsights() {
                if (!this.insightsIsReady) {
                    return;
                }

                this.insights.sending = true;

                try {
                    await useStoryInsights().postCampaignInsights(this.proposal.id, this.job.id, {
                        files: this.uploadedFiles,
                        metrics: this.getMetricsWithoutCommas()
                    });

                    this.$emit('reload-proposal');
                    message.success('Your Story Insights have been delivered!');
                    this.resetInsights();
                    this.onPageClose();
                } catch (e) {
                    message.error(
                        'There was an error while trying to deliver your story insights, please try again later.'
                    );
                }

                this.insights.sending = false;
            },
            onPageClose() {
                this.$router.push({ name: 'v2.campaign.proposal.tasks' });
            }
        }
    };
</script>

<style lang="scss" scoped>
    .margin-bottom-small {
        margin-bottom: $g-spacing-3;
    }

    .margin-bottom-normal {
        margin-bottom: $g-spacing-5;
    }

    .margin-bottom-large {
        margin-bottom: $g-spacing-6;
    }

    ::v-deep .ant-upload.ant-upload-select {
        display: block;
        margin-bottom: $g-spacing-5;
    }

    ::v-deep .ant-form-item {
        padding-bottom: $g-spacing-5;
        margin-bottom: 0;
    }
</style>
